"use client";

import React from "react";
import { Check, Info, PhoneCall, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const plans = [{
  id: 1,
  name: "Basic Protection",
  description: "Essential protection for common pests",
  highlight: "",
  isPopular: false,
  priceQuarterly: 99
}, {
  id: 2,
  name: "Advanced Protection",
  description: "Enhanced protection for extra peace of mind",
  highlight: "Most Popular",
  isPopular: true,
  priceQuarterly: 129
}, {
  id: 3,
  name: "Premium Protection",
  description: "Our most comprehensive protection plan",
  highlight: "Maximum Protection",
  isPopular: false,
  priceQuarterly: 159
}];
const features = [{
  planId: 1,
  text: "Included",
  type: "header"
}, {
  planId: 1,
  text: "Quarterly billing: $99/quarter",
  type: "included"
}, {
  planId: 1,
  text: "General Pest Control (ants, roaches, spiders)",
  type: "included"
}, {
  planId: 1,
  text: "Exterior Barrier Treatment",
  type: "included"
}, {
  planId: 1,
  text: "Basic Pest Prevention",
  type: "included"
}, {
  planId: 1,
  text: "Not Included",
  type: "header"
}, {
  planId: 1,
  text: "Web Removal",
  type: "not_included"
}, {
  planId: 1,
  text: "Wasp Nest Removal",
  type: "not_included"
}, {
  planId: 1,
  text: "Ant Treatment (Interior & Exterior)",
  type: "not_included"
}, {
  planId: 1,
  text: "Rodent Control",
  type: "not_included"
}, {
  planId: 1,
  text: "Seasonal Pest Prevention",
  type: "not_included"
}, {
  planId: 1,
  text: "Available Upon Request",
  type: "header"
}, {
  planId: 1,
  text: "Interior Treatment",
  type: "request"
}, {
  planId: 1,
  text: "Garage Treatment",
  type: "request"
}, {
  planId: 1,
  text: "Crawlspace Treatment",
  type: "request"
}, {
  planId: 1,
  text: "Free Reservice",
  type: "request"
}, {
  planId: 2,
  text: "Includes Everything in Basic Plus:",
  type: "header"
}, {
  planId: 2,
  text: "Quarterly billing: $129/quarter",
  type: "included"
}, {
  planId: 2,
  text: "Web Removal",
  type: "included"
}, {
  planId: 2,
  text: "Wasp Nest Removal",
  type: "included"
}, {
  planId: 2,
  text: "Ant Treatment (Interior & Exterior)",
  type: "included"
}, {
  planId: 2,
  text: "Rodent Control",
  type: "included"
}, {
  planId: 2,
  text: "Seasonal Pest Prevention",
  type: "included"
}, {
  planId: 2,
  text: "Additional Entry Point Protection",
  type: "included"
}, {
  planId: 2,
  text: "Not Included",
  type: "header"
}, {
  planId: 2,
  text: "Lawn Treatment",
  type: "not_included"
}, {
  planId: 2,
  text: "Pantry Pest Control",
  type: "not_included"
}, {
  planId: 2,
  text: "Yellow Jacket Control",
  type: "not_included"
}, {
  planId: 2,
  text: "Other Specialty Pests",
  type: "not_included"
}, {
  planId: 2,
  text: "Available Upon Request",
  type: "header"
}, {
  planId: 2,
  text: "Interior Treatment",
  type: "request"
}, {
  planId: 2,
  text: "Garage Treatment",
  type: "request"
}, {
  planId: 2,
  text: "Crawlspace Treatment",
  type: "request"
}, {
  planId: 2,
  text: "Free Reservice",
  type: "request"
}, {
  planId: 3,
  text: "Includes Everything in Advanced Plus:",
  type: "header"
}, {
  planId: 3,
  text: "Quarterly billing: $159/quarter",
  type: "included"
}, {
  planId: 3,
  text: "Lawn Treatment",
  type: "included"
}, {
  planId: 3,
  text: "Pantry Pest Control",
  type: "included"
}, {
  planId: 3,
  text: "Yellow Jacket Control",
  type: "included"
}, {
  planId: 3,
  text: "Mite Treatment",
  type: "included"
}, {
  planId: 3,
  text: "Snake Prevention",
  type: "included"
}, {
  planId: 3,
  text: "Other Pest Control & Specialty Services",
  type: "included"
}, {
  planId: 3,
  text: "Available Upon Request",
  type: "header"
}, {
  planId: 3,
  text: "Interior Treatment",
  type: "request"
}, {
  planId: 3,
  text: "Garage Treatment",
  type: "request"
}, {
  planId: 3,
  text: "Crawlspace Treatment",
  type: "request"
}, {
  planId: 3,
  text: "Free Reservice",
  type: "request"
}];
export default function ProtectionPlansComparison() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="protectionplanscomparison" className="bg-background py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-12 md:mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground mb-4">
            Choose Your Protection Plan
          </h2>
          <p className="text-lg text-muted-foreground">
            Comprehensive pest control solutions tailored to your home's specific needs. All plans include scheduled quarterly service.
          </p>
        </div>

        <TooltipProvider>
          <div ref={gridRef} className={`grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto transition-all duration-1000 ease-out delay-200 ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            {plans.map((plan, index) => {
            const planFeatures = features.filter(f => f.planId === plan.id);
            const monthlyPrice = Math.round(plan.priceQuarterly / 3);
            return <Card key={plan.id} data-index={index} className={`relative flex flex-col h-full rounded-md shadow-lg transition-transform duration-300 hover:-translate-y-1 ${plan.isPopular ? "border-2 border-primary bg-accent/10 shadow-xl z-10 md:-mt-4 md:mb-4" : "border border-border bg-card"}`}>
                  {plan.highlight && <div className="absolute -top-2 sm:-top-3 left-0 right-0 flex justify-center">
                      <Badge variant={plan.isPopular ? "default" : "secondary"} className="px-4 py-1 text-sm font-semibold uppercase tracking-wider shadow-sm">
                        {plan.highlight}
                      </Badge>
                    </div>}

                  <CardHeader className={`text-center pb-4 ${plan.highlight ? "pt-8" : "pt-6"}`}>
                    <CardTitle className="text-xl sm:text-2xl font-bold font-default text-foreground">
                      {plan.name}
                    </CardTitle>
                    <CardDescription className="text-sm mt-2 min-h-[40px]">
                      {plan.description}
                    </CardDescription>
                    <div className={`mt-4 p-3 sm:p-4 rounded-md text-center transition-colors ${plan.isPopular ? "bg-accent/40 border border-primary/40 shadow-xs" : "bg-accent/20 border border-border/60"}`}>
                      <div className="flex items-baseline justify-center gap-1.5">
                        <span className="text-4xl sm:text-5xl font-extrabold text-primary tracking-tight">
                          ${plan.priceQuarterly}
                        </span>
                        <span className="text-sm sm:text-base font-medium text-muted-foreground">
                          /quarter
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1.5">
                        Just <span className="font-semibold text-foreground">${monthlyPrice}/mo</span>, billed quarterly
                      </p>
                    </div>
                  </CardHeader>

                  <CardContent className="flex-grow px-4 py-3 sm:px-6 sm:py-4">
                    <ul className="space-y-3">
                      {planFeatures.map((feature, fIndex) => <li key={fIndex} data-index={fIndex} className={`flex items-start ${feature.type === "header" ? "pt-4 pb-1" : ""}`}>
                          {feature.type === "header" ? <span className="text-sm font-bold font-default text-foreground uppercase tracking-wide w-full border-b border-border pb-1">
                              {feature.text}
                            </span> : <>
                              <span className="mr-3 mt-0.5 shrink-0">
                                {feature.type === "included" && <Check className="w-5 h-5 text-primary" />}
                                {feature.type === "not_included" && <X className="w-5 h-5 text-muted-foreground/50" />}
                                {feature.type === "request" && <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Info className="w-5 h-5 text-primary/70 cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Available upon request during service</p>
                                    </TooltipContent>
                                  </Tooltip>}
                              </span>
                              <span className={`text-sm leading-tight ${feature.type === "not_included" ? "text-muted-foreground line-through opacity-70" : "text-foreground"}`}>
                                {feature.text}
                              </span>
                            </>}
                        </li>)}
                    </ul>
                  </CardContent>

                  <CardFooter className="flex flex-col gap-4 pt-6 pb-8 px-6 mt-auto border-t border-border/50 bg-muted/20 rounded-b-md">
                    <div className="text-center w-full">
                      <p className="text-sm font-medium text-foreground mb-1">
                        Quarterly Service Plan
                      </p>
                      <p className="text-xs text-muted-foreground mb-4">
                        Billed per treatment
                      </p>
                    </div>
                    <Button asChild className={`w-full rounded-md h-11 sm:h-12 min-h-[44px] text-base font-semibold shadow-md transition-all ${plan.isPopular ? "bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-lg" : "bg-secondary text-secondary-foreground hover:bg-secondary/90"}`}>
                      <Link to="tel:9198961953">
                        <PhoneCall className="w-5 h-5 mr-2" />
                        Call for Free Inspection
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>;
          })}
          </div>
        </TooltipProvider>
      </div>
    </section>;
}